// Optimistic send / edit / delete / mark-read for the messenger (web, mobile
// and kiosk). Pure functions over the cached shapes; the hooks own the
// QueryClient plumbing.
//
// Why: every messenger action used to wait out the whole round trip before
// the screen changed, and then re-downloaded EVERY loaded page of the thread
// (50 messages a page, each with attachments, reactions and read receipts) to
// learn about the one row that changed. On a PIN kiosk the send also went
// through a cold `limited-message-send` boot and then re-fetched up to 200
// messages. The composer held the typed text until all of that finished, so a
// send looked like it had not happened for seconds.
//
// The approach mirrors optimistic-reactions.ts: paint the cache the instant
// the user acts, roll back ONLY the thing we painted on error, and let the
// realtime channel / polling fallback reconcile. No blanket invalidate.
//
// ---------------------------------------------------------------------------
// Lifecycle of a sent message in the cache
//
//   1. 'sending'  id = `pending-message:<clientId>`. Painted in onMutate. The
//                 row has NO attachments even when files were picked — we
//                 never fake an uploaded URL; `pending_attachment_count` lets
//                 the bubble say "uploading N".
//   2. 'sent'     The send RPC returned the real id. We swap the id in place
//                 (so edit / delete / reply / react on it hit a real row) and
//                 keep `local_status: 'sent'` to mark that the row is still
//                 our local copy, not the server's hydrated one.
//   3. (none)     The server's own row replaced ours — via the realtime INSERT
//                 (patchMessageInsert), a one-row hydrate after the send, or
//                 the next poll. From here it is an ordinary message.
//
// send_message (and limited-message-send) do NOT accept a client-generated id,
// so the realtime INSERT can arrive BEFORE the RPC returns and we cannot key
// on the id at that point. mergeServerMessage therefore also matches a
// 'sending' row by (sender, body) — the oldest such row, so two identical
// "ok"s sent back to back each resolve to their own server row in order.
// ---------------------------------------------------------------------------

import type { InfiniteData } from '@tanstack/react-query';
import type {
  ConversationListEntry,
  MessageWithAttachments,
  ReplyPreview,
  UnreadSummaryRow,
} from '@/types/messaging';

type Pages = InfiniteData<MessageWithAttachments[], string | null>;

const PENDING_PREFIX = 'pending-message:';

export const pendingMessageId = (clientId: string) => `${PENDING_PREFIX}${clientId}`;

/** A bubble painted by a send whose RPC has not returned yet. */
export const isPendingMessage = (m: Pick<MessageWithAttachments, 'id'>) =>
  m.id.startsWith(PENDING_PREFIX);

/** Our local copy (sending or sent) rather than a row the server gave us. */
export const isLocalMessage = (m: Pick<MessageWithAttachments, 'local_status'>) => !!m.local_status;

export const newClientId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    // Old WebViews on store kiosks can lack randomUUID; uniqueness within one
    // tab is all this id needs.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
};

export interface PendingMessageInput {
  clientId: string;
  conversationId: string;
  senderId: string;
  body: string;
  /** ISO timestamp for the bubble; replaced by the server's on reconcile. */
  now: string;
  replyTo?: ReplyPreview | null;
  attachmentCount?: number;
}

export function buildPendingMessage(input: PendingMessageInput): MessageWithAttachments {
  const attachmentCount = input.attachmentCount ?? 0;
  return {
    id: pendingMessageId(input.clientId),
    conversation_id: input.conversationId,
    sender_id: input.senderId,
    body: input.body,
    created_at: input.now,
    edited_at: null,
    deleted_at: null,
    deleted_by: null,
    reply_to_id: input.replyTo?.id ?? null,
    reply_to: input.replyTo ?? null,
    attachments: [],
    reactions: [],
    read_receipts: [],
    local_status: 'sending',
    ...(attachmentCount > 0 ? { pending_attachment_count: attachmentCount } : {}),
  };
}

/** Reply-quote preview for a pending bubble, from whatever is already cached. */
export const replyPreviewFrom = (
  m: MessageWithAttachments | undefined | null,
): ReplyPreview | null =>
  m ? { id: m.id, sender_id: m.sender_id, body: m.body, deleted_at: m.deleted_at } : null;

// --- generic list helpers ---------------------------------------------------
//
// The web/mobile cache is an infinite query whose pages are ordered NEWEST
// FIRST (page 0 = newest). The kiosk cache is one flat list ordered OLDEST
// FIRST. The helpers below work on a flat list plus an `insert` strategy so
// the two shapes share one implementation.

const findOldestPendingMatch = (
  list: MessageWithAttachments[],
  msg: MessageWithAttachments,
  newestFirst: boolean,
): number => {
  const matches = (m: MessageWithAttachments) =>
    isPendingMessage(m) && m.sender_id === msg.sender_id && m.body === msg.body;
  if (newestFirst) {
    for (let i = list.length - 1; i >= 0; i--) if (matches(list[i])) return i;
    return -1;
  }
  return list.findIndex(matches);
};

// --- infinite query (web + mobile) -----------------------------------------

/** Paint a pending bubble at the top of page 0. No cache → nothing to paint. */
export function insertPendingIntoPages(data: Pages | undefined, msg: MessageWithAttachments): Pages | undefined {
  if (!data) return data;
  const pages = data.pages.length === 0 ? [[msg]] : [[msg, ...data.pages[0]], ...data.pages.slice(1)];
  return { ...data, pages };
}

/**
 * Merge a server message row (realtime INSERT, post-send hydrate) into the
 * pages. In order:
 *   - same id already cached: replace it if it is our local copy (so the
 *     hydrated attachments land), otherwise leave the cache untouched (the
 *     old dedupe);
 *   - a 'sending' bubble from the same sender with the same body: replace the
 *     oldest one — this is the realtime-beat-the-RPC case;
 *   - otherwise prepend to page 0 (a message from somebody else).
 */
export function mergeServerMessageIntoPages(
  data: Pages | undefined,
  msg: MessageWithAttachments,
): Pages | undefined {
  if (!data) return data;
  for (let p = 0; p < data.pages.length; p++) {
    const idx = data.pages[p].findIndex((m) => m.id === msg.id);
    if (idx === -1) continue;
    if (!isLocalMessage(data.pages[p][idx])) return data;
    const page = data.pages[p].slice();
    page[idx] = msg;
    const pages = data.pages.slice();
    pages[p] = page;
    return { ...data, pages };
  }
  // Pending bubbles only ever live in page 0 (that is where we paint them).
  const page0 = data.pages[0] ?? [];
  const pendingIdx = findOldestPendingMatch(page0, msg, true);
  if (pendingIdx !== -1) {
    const page = page0.slice();
    page[pendingIdx] = msg;
    return { ...data, pages: [page, ...data.pages.slice(1)] };
  }
  return insertPendingIntoPages(data, msg);
}

export type ConfirmOutcome =
  /** The server's row was already cached (realtime won); the bubble is gone. */
  | 'already-merged'
  /** Swapped the bubble's id for the real one. */
  | 'swapped'
  /** Neither the bubble nor the real row is cached (a refetch wiped it, or
   *  realtime matched our row to a different bubble). Caller should fetch the
   *  row and merge it. */
  | 'missing';

const confirmInList = (
  list: MessageWithAttachments[],
  pendingId: string,
  realId: string,
): { list: MessageWithAttachments[]; outcome: ConfirmOutcome } => {
  if (list.some((m) => m.id === realId)) {
    const next = list.filter((m) => m.id !== pendingId);
    return { list: next.length === list.length ? list : next, outcome: 'already-merged' };
  }
  const idx = list.findIndex((m) => m.id === pendingId);
  if (idx === -1) return { list, outcome: 'missing' };
  const next = list.slice();
  next[idx] = { ...list[idx], id: realId, local_status: 'sent' };
  return { list: next, outcome: 'swapped' };
};

/** The send RPC returned `realId` for the bubble painted as `pendingId`. */
export function confirmPendingInPages(
  data: Pages | undefined,
  pendingId: string,
  realId: string,
): { data: Pages | undefined; outcome: ConfirmOutcome } {
  if (!data) return { data, outcome: 'missing' };
  const flat = data.pages.flat();
  if (flat.some((m) => m.id === realId)) {
    let touched = false;
    const pages = data.pages.map((page) => {
      const next = page.filter((m) => m.id !== pendingId);
      if (next.length === page.length) return page;
      touched = true;
      return next;
    });
    return { data: touched ? { ...data, pages } : data, outcome: 'already-merged' };
  }
  const page0 = data.pages[0] ?? [];
  const res = confirmInList(page0, pendingId, realId);
  if (res.outcome !== 'swapped') return { data, outcome: res.outcome };
  return { data: { ...data, pages: [res.list, ...data.pages.slice(1)] }, outcome: 'swapped' };
}

/** Take a failed send's bubble back out. Touches nothing else. */
export function removeMessageFromPages(data: Pages | undefined, id: string): Pages | undefined {
  if (!data) return data;
  let touched = false;
  const pages = data.pages.map((page) => {
    const next = page.filter((m) => m.id !== id);
    if (next.length === page.length) return page;
    touched = true;
    return next;
  });
  return touched ? { ...data, pages } : data;
}

/**
 * Shallow-patch one message wherever it is in the pages. Returns the
 * message as it was BEFORE the patch so the caller can roll back just that
 * row (restoring a whole-cache snapshot would also undo realtime inserts
 * that landed while the request was in flight).
 */
export function patchMessageInPages(
  data: Pages | undefined,
  id: string,
  patch: Partial<MessageWithAttachments>,
): { data: Pages | undefined; before: MessageWithAttachments | null } {
  if (!data) return { data, before: null };
  for (let p = 0; p < data.pages.length; p++) {
    const idx = data.pages[p].findIndex((m) => m.id === id);
    if (idx === -1) continue;
    const before = data.pages[p][idx];
    const page = data.pages[p].slice();
    page[idx] = { ...before, ...patch };
    const pages = data.pages.slice();
    pages[p] = page;
    return { data: { ...data, pages }, before };
  }
  return { data, before: null };
}

/**
 * A poll (or refetch-on-focus) of page 0 replaces the page wholesale. Carry
 * our local rows across it so a bubble does not vanish mid-send and reappear
 * a moment later:
 *   - a 'sent' row survives unless the fetched page already has its id;
 *   - a 'sending' row survives unless the fetched page has a row from the
 *     same sender with the same body created no earlier than a minute before
 *     the bubble (the RPC committed and this poll saw it; the minute absorbs
 *     client/server clock skew).
 * Returned in the same order convention as `fetched`.
 */
export function carryLocalMessages(
  fetched: MessageWithAttachments[],
  previous: MessageWithAttachments[] | undefined,
  order: 'newest-first' | 'oldest-first',
): MessageWithAttachments[] {
  const local = (previous ?? []).filter(isLocalMessage);
  if (local.length === 0) return fetched;
  const ids = new Set(fetched.map((m) => m.id));
  const claimed = new Set<string>();
  const SKEW_MS = 60_000;
  const survivors = local.filter((m) => {
    if (ids.has(m.id)) return false;
    if (m.local_status !== 'sending') return true;
    const floor = new Date(m.created_at).getTime() - SKEW_MS;
    const match = fetched.find(
      (f) =>
        !claimed.has(f.id) &&
        f.sender_id === m.sender_id &&
        f.body === m.body &&
        new Date(f.created_at).getTime() >= floor,
    );
    if (!match) return true;
    claimed.add(match.id);
    return false;
  });
  if (survivors.length === 0) return fetched;
  // Local rows are always the newest thing in the thread from this client's
  // point of view.
  return order === 'newest-first' ? [...survivors, ...fetched] : [...fetched, ...survivors];
}

// --- kiosk flat list (oldest first) ----------------------------------------

export function appendPendingToList(
  list: MessageWithAttachments[] | undefined,
  msg: MessageWithAttachments,
): MessageWithAttachments[] | undefined {
  if (!list) return list;
  return [...list, msg];
}

/** Kiosk twin of mergeServerMessageIntoPages. */
export function mergeServerMessageIntoList(
  list: MessageWithAttachments[] | undefined,
  msg: MessageWithAttachments,
): MessageWithAttachments[] | undefined {
  if (!list) return list;
  const idx = list.findIndex((m) => m.id === msg.id);
  if (idx !== -1) {
    if (!isLocalMessage(list[idx])) return list;
    const next = list.slice();
    next[idx] = msg;
    return next;
  }
  const pendingIdx = findOldestPendingMatch(list, msg, false);
  if (pendingIdx !== -1) {
    const next = list.slice();
    next[pendingIdx] = msg;
    return next;
  }
  return [...list, msg];
}

export function confirmPendingInList(
  list: MessageWithAttachments[] | undefined,
  pendingId: string,
  realId: string,
): { list: MessageWithAttachments[] | undefined; outcome: ConfirmOutcome } {
  if (!list) return { list, outcome: 'missing' };
  return confirmInList(list, pendingId, realId);
}

export function removeMessageFromList(
  list: MessageWithAttachments[] | undefined,
  id: string,
): MessageWithAttachments[] | undefined {
  if (!list) return list;
  const next = list.filter((m) => m.id !== id);
  return next.length === list.length ? list : next;
}

// --- unread counts (mark-read) ---------------------------------------------

type UnreadRow = Pick<ConversationListEntry | UnreadSummaryRow, 'conversation_id' | 'unread_count'>;

/**
 * Zero one conversation's unread count in a conversation list or unread
 * summary. mark_conversation_read (and limited-messages' mark_read) move the
 * read pointer to now, so 0 is exactly what the next poll will say. Returns
 * the same array when nothing changes so React Query skips the re-render.
 */
export function zeroUnreadFor<T extends UnreadRow>(rows: T[] | undefined, conversationId: string): T[] | undefined {
  if (!rows) return rows;
  let touched = false;
  const next = rows.map((r) => {
    if (r.conversation_id !== conversationId || !r.unread_count) return r;
    touched = true;
    return { ...r, unread_count: 0 };
  });
  return touched ? next : rows;
}

// --- composer ----------------------------------------------------------------

/**
 * Put a failed send's text back in the composer. The composer cleared the
 * moment Send was pressed, so the user may already be typing the next
 * message; never overwrite that — put the failed text first, on its own
 * paragraph, so both survive and the user can decide.
 */
export function restoreFailedDraft(current: string, failed: string): string {
  if (!failed) return current;
  if (current.trim().length === 0) return failed;
  if (current.startsWith(failed)) return current;
  return `${failed}\n\n${current}`;
}
